import { useState, useEffect } from "react";
import { Modal, Button } from "antd";
import VirtualKeyboard from "../VirtualKeyboard";
import {
  ModalContent,
  CodeInput,
  InputContainer,
  ButtonContainer,
  Title,
  Description,
  GlobalModalStyles,
  ReasonSelect,
} from "./styles";
import { useIntl } from "react-intl";

const ACTIVE_FIELD = {
  ACCESS_CODE: "ACCESS_CODE",
  JUSTIFICATION: "JUSTIFICATION",
};

const AccessCodeConfirmationModal = ({
  placeholder = "",
  visible,
  onCancel,
  onConfirm,
  title = "",
  description = "",
  typePassword = false,
  isReprint = false,
  isLoadingButton = false,
}) => {
  const intl = useIntl();
  const [accessCode, setAccessCode] = useState("");
  const [justification, setJustification] = useState();
  const [selectedReason, setSelectedReason] = useState();
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [activeField, setActiveField] = useState(null);

  useEffect(() => {
    if (!visible) {
      setAccessCode("");
      setJustification("");
      setSelectedReason(null);
      setShowKeyboard(false);
      setActiveField(null);
    }
  }, [visible]);

  /**
   * Controle de foco lógico (não visual)
   */
  const handleFieldFocus = (field) => {
    setActiveField(field);
    setShowKeyboard(true);
  };

  const handleKeyboardInput = (key) => {
    if (activeField === ACTIVE_FIELD.ACCESS_CODE) {
      setAccessCode((prev) => prev + key);
    }

    if (activeField === ACTIVE_FIELD.JUSTIFICATION) {
      setJustification((prev) => prev + key);
    }
  };

  const handleKeyboardBackspace = () => {
    if (activeField === ACTIVE_FIELD.ACCESS_CODE) {
      setAccessCode((prev) => prev.slice(0, -1));
    }

    if (activeField === ACTIVE_FIELD.JUSTIFICATION) {
      setJustification((prev) => prev.slice(0, -1));
    }
  };

  const handleKeyboardClear = () => {
    if (activeField === ACTIVE_FIELD.ACCESS_CODE) {
      setAccessCode("");
    }

    if (activeField === ACTIVE_FIELD.JUSTIFICATION) {
      setJustification("");
    }
  };

  const handleKeyboardClose = () => {
    setShowKeyboard(false);
    setActiveField(null);
  };

  const handleConfirm = () => {
    if (!accessCode.trim()) return;

    if (!isReprint) {
      onConfirm(accessCode);
      return;
    }

    onConfirm({
      password: accessCode,
      action: selectedReason,
      justification,
    });
  };

  const handleModalCancel = () => {
    onCancel();
  };

  const printingIssueOptions = [
    {
      value: "PRINTING_ISSUE_CAUSED_BY_PHYSICAL_PROBLEMS",
      label: intl.formatMessage({ id: "printing_issue.physical" }),
    },
    {
      value: "PRINTING_ISSUE_CAUSED_BY_SOFTWARE_PROBLEMS",
      label: intl.formatMessage({ id: "printing_issue.software" }),
    },
    {
      value: "PRINTING_ISSUE_CAUSED_BY_OTHER_PROBLEMS",
      label: intl.formatMessage({ id: "printing_issue.other" }),
    },
  ];

  return (
    <>
      <GlobalModalStyles />

      <Modal
        title={null}
        open={visible}
        onCancel={handleModalCancel}
        footer={null}
        maskClosable={false}
        width={520}
        style={{ zIndex: 1000, top: "10vh" }}
        className="access-code-modal"
      >
        <ModalContent>
          <Title>{title}</Title>
          <Description>{description}</Description>

          <InputContainer>
            {/* Código de acesso */}
            <CodeInput
              type={typePassword ? "password" : "text"}
              placeholder={placeholder}
              value={accessCode}
              readOnly
              autoComplete="off"
              onFocus={() => handleFieldFocus(ACTIVE_FIELD.ACCESS_CODE)}
              onKeyDown={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
            />

            {isReprint && (
              <>
                {/* Motivo */}
                <ReasonSelect
                  value={selectedReason}
                  options={printingIssueOptions}
                  placeholder={intl.formatMessage({ id: "select_reason" })}
                  onChange={setSelectedReason}
                  showSearch={false}
                />

                {/* Justificativa */}
                <CodeInput
                  justification
                  placeholder={intl.formatMessage({ id: "justification" })}
                  value={justification}
                  readOnly
                  autoComplete="off"
                  onFocus={() => handleFieldFocus(ACTIVE_FIELD.JUSTIFICATION)}
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                />
              </>
            )}
          </InputContainer>

          <ButtonContainer>
            <Button size="large" onClick={handleModalCancel}>
              {intl.formatMessage({ id: "cancel" })}
            </Button>

            <Button
              loading={isLoadingButton}
              type="primary"
              size="large"
              onClick={handleConfirm}
              disabled={
                !isReprint
                  ? !accessCode.trim()
                  : !accessCode.trim() || !selectedReason
              }
            >
              {intl.formatMessage({ id: "confirm" })}
            </Button>
          </ButtonContainer>
        </ModalContent>
      </Modal>

      <VirtualKeyboard
        isVisible={showKeyboard}
        onKeyPress={handleKeyboardInput}
        onBackspace={handleKeyboardBackspace}
        onClear={handleKeyboardClear}
        onClose={handleKeyboardClose}
        value={
          activeField === ACTIVE_FIELD.ACCESS_CODE ? accessCode : justification
        }
      />
    </>
  );
};

export default AccessCodeConfirmationModal;
